import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { api, ApiError, type Attempt, type ProfilePayload } from "../api.ts";
import { BANK } from "../bank/index.ts";
import { TRACKS } from "../bank/tracks.ts";
import { usePageMeta } from "../components/Layout.tsx";

const OUTCOME = ["Failed", "Hard", "Good", "Easy"] as const;

/** Falls back to the stored id, so an attempt on a retired drill still lists. */
const titleOf = (drillId: string) => BANK.find((d) => d.id === drillId)?.title ?? drillId;

function minutesLabel(ms: number): string {
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, "0")}s`;
}

function whenLabel(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function Profile() {
  usePageMeta("Profile", "Your scores, your weakest patterns, and your scoreboard listing.");
  const [data, setData] = useState<ProfilePayload | null>(null);
  const [history, setHistory] = useState<Attempt[] | null>(null);
  const [handle, setHandle] = useState("");
  const [listed, setListed] = useState(false);
  const [tracks, setTracks] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.profile().then((payload) => {
      if (cancelled) return;
      setData(payload);
      setHandle(payload.profile.handle ?? "");
      setListed(payload.profile.onLeaderboard);
      setTracks(payload.profile.tracks);
    });
    api
      .history(50)
      .then((payload) => !cancelled && setHistory(payload.attempts))
      // The log is supporting detail; failing to load it should not blank the
      // whole page, which the stats above do not depend on.
      .catch(() => !cancelled && setHistory([]));
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const trimmed = handle.trim();
      const saved = await api.saveProfile({
        handle: trimmed === "" ? null : trimmed.toLowerCase(),
        onLeaderboard: listed,
        tracks,
      });
      setHandle(saved.handle ?? "");
      setListed(saved.onLeaderboard);
      setTracks(saved.tracks);
      setMessage({
        kind: "ok",
        text: saved.onLeaderboard
          ? `Listed on the scoreboard as ${saved.handle}.`
          : "Saved. You are not listed on the scoreboard.",
      });
    } catch (cause) {
      setMessage({
        kind: "error",
        text:
          cause instanceof ApiError && cause.status === 409
            ? "That handle is taken or reserved. Pick another."
            : cause instanceof ApiError && cause.status === 400
              ? "Handles are 3 to 20 characters: letters, numbers, hyphens and underscores."
              : "Could not save. Try again in a moment.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="muted">Loading…</p>;

  const { account, patterns, stats } = data;
  // Weakest first: a pattern you attempt often and pass rarely is exactly what
  // the next session should be spent on, and it is invisible in the total score.
  const ranked = [...patterns].sort(
    (a, b) => a.passes / Math.max(1, a.attempts) - b.passes / Math.max(1, b.attempts),
  );

  return (
    <article className="prose-page">
      <h1>Profile</h1>
      <p className="muted">
        {account.email}
        {account.joinedAt && ` · joined ${new Date(account.joinedAt).toLocaleDateString()}`}
      </p>

      <div className="stat-cards">
        <div>
          <strong>{stats.totalScore.toLocaleString()}</strong>
          <span>total score</span>
        </div>
        <div>
          <strong>{stats.dayStreak}</strong>
          <span>day streak</span>
        </div>
        <div>
          <strong>{stats.totalAttempts}</strong>
          <span>attempts</span>
        </div>
        <div>
          <strong>{stats.dueNow}</strong>
          <span>due now</span>
        </div>
      </div>

      <h2>Patterns, weakest first</h2>
      {ranked.length === 0 ? (
        <p className="muted">
          Nothing yet. <Link to="/drill">Do a drill</Link> and this fills in.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="board">
            <thead>
              <tr>
                <th>Pattern</th>
                <th className="num">Attempts</th>
                <th className="num">Passed</th>
                <th className="num">Rate</th>
                <th className="num">Score</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((p) => {
                const rate = Math.round((p.passes / Math.max(1, p.attempts)) * 100);
                return (
                  <tr key={p.pattern}>
                    <td>{p.pattern}</td>
                    <td className="num">{p.attempts}</td>
                    <td className="num">{p.passes}</td>
                    <td className={`num ${rate < 50 ? "weak" : ""}`}>{rate}%</td>
                    <td className="num">{p.score.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <h2>Tracks</h2>
      <p className="muted small">
        The scheduler only ever serves problems from the tracks you are in, so a subject you never
        signed up for cannot turn up mid-session or count as due.
      </p>
      <form className="panel form inline-form" onSubmit={save}>
        {TRACKS.map((t) => {
          const count = BANK.filter((d) => d.track === t.id).length;
          const enrolled = tracks.includes(t.id);
          return (
            <label className="check" key={t.id}>
              <input
                type="checkbox"
                checked={enrolled}
                disabled={!t.available}
                onChange={(e) =>
                  setTracks((prev) =>
                    e.target.checked ? [...prev, t.id] : prev.filter((id) => id !== t.id),
                  )
                }
              />
              <span>
                <strong>{t.name}</strong>{" "}
                <span className="muted">
                  · {count} {count === 1 ? "problem" : "problems"}
                  {!t.available && " · not ready yet"}
                </span>
                <br />
                <span className="hint-text">{t.blurb}</span>
              </span>
            </label>
          );
        })}
        {tracks.length === 0 && (
          <p className="error">Pick at least one track, or there is nothing to practise.</p>
        )}
        <button className="primary" type="submit" disabled={saving || tracks.length === 0}>
          {saving ? "Saving…" : "Save"}
        </button>
      </form>

      <h2>Recent attempts</h2>
      <p className="muted small">
        Every attempt, whatever happened. Minutes and runs are what the score is built from, so this
        is the log behind the numbers above rather than a separate tally.
      </p>
      {history === null ? (
        <p className="muted">Loading…</p>
      ) : history.length === 0 ? (
        <p className="muted">
          Nothing yet. <Link to="/drill">Do a drill</Link> and it shows up here.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="board">
            <thead>
              <tr>
                <th>When</th>
                <th>Problem</th>
                <th className="num">Time</th>
                <th className="num">Runs</th>
                <th>What happened</th>
                <th className="num">Score</th>
              </tr>
            </thead>
            <tbody>
              {history.map((a) => (
                <tr key={a.id}>
                  <td className="muted">{whenLabel(a.createdAt)}</td>
                  <td>{titleOf(a.drillId)}</td>
                  <td className="num">{minutesLabel(a.elapsedMs)}</td>
                  <td className="num">{a.runs}</td>
                  <td>
                    <span className={`verdict-tag grade-${a.grade}`}>{OUTCOME[a.grade]}</span>
                    {a.revealed && <span className="tag-note">revealed</span>}
                    {a.ahead && !a.revealed && <span className="tag-note">early</span>}
                  </td>
                  <td className="num">{a.score > 0 ? a.score.toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Scoreboard listing</h2>
      <form className="panel form inline-form" onSubmit={save}>
        <p className="muted">
          You are not listed anywhere unless you switch this on. Listing publishes your handle and your
          scores. It never publishes your name or your email.
        </p>
        <label>
          <span>Handle</span>
          <input
            type="text"
            value={handle}
            placeholder="e.g. quiet-heron"
            maxLength={20}
            onChange={(e) => setHandle(e.target.value)}
          />
          <span className="hint-text">
            3 to 20 characters: letters, numbers, hyphens and underscores. Clearing it removes you from
            the scoreboard.
          </span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={listed}
            onChange={(e) => setListed(e.target.checked)}
            disabled={handle.trim() === ""}
          />
          <span>
            Show me on the public <Link to="/scoreboard">scoreboard</Link>
          </span>
        </label>
        {message && <p className={message.kind === "ok" ? "ok" : "error"}>{message.text}</p>}
        <button className="primary" type="submit" disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
      </form>
    </article>
  );
}
